import { RevealSensitiveButton } from '../components/RevealSensitiveButton'
import { ContactEarningsPanel } from '../components/MandantePaymentsPanel'
import { CustomerValuePanel } from '../components/CustomerValuePanel'
import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  contactOverviewApi,
  type AttributionTouch,
  type ActionPriority,
  type ContactOverview,
  type NextAction,
} from '../api/contactOverview'
import type { LoginResponse } from '../api/auth'
import { ContactDynamicFields } from '../components/ContactDynamicFields'
import { CommercialBlockPanel } from '../components/CommercialBlockPanel'
import { ReferralPanel } from '../components/ReferralPanel'
import { UnoMobilePanel } from '../components/UnoMobilePanel'
import { MoveToTrashButton, TrashBanner } from '../components/TrashControls'
import { PrintShareButtons } from '../components/PrintShareButtons'
import {
  Button,
  ButtonLink,
  Callout,
  Card,
  CardContent,
  CardHeader,
  Icon,
  LoadingState,
  Page as PageShell,
  List,
  ListItem,
  PageHeader,
  PaneLayout,
  CardGrid,
  KeyValueList,
  StatusPill,
  type PillTone,
} from '../components/ui'
import { ContactArchiveTree } from '../components/ContactArchiveTree'
import { useFeatureFlag } from '../lib/featureFlags'

/**
 * F-076 (TASK-0330) — scheda cliente 360 (`/contacts/:id`). Il cliente è un contatto (`crm.contacts`).
 * Mostra azioni consigliate con la loro motivazione, prima e ultima attribuzione, settori posseduti,
 * suggerimenti di cross-selling, pratiche visibili e la cronologia unica. Nessun importo: il server non ne
 * invia. Un cliente non visibile al chiamante risponde 404 e la pagina lo dice senza rivelare altro.
 */

const ROME = 'Europe/Rome'

function formatRome(iso: string): string {
  return new Date(iso).toLocaleString('it-IT', {
    timeZone: ROME, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString('it-IT', { timeZone: ROME })
}

const PRIORITY_TONE: Record<ActionPriority, PillTone> = {
  Alta: 'error',
  Media: 'warning',
  Bassa: 'neutral',
}

const KIND_ICONS: Record<string, string> = {
  lead_creato: 'person_search',
  lead_stato_iniziale: 'inventory_2',
  lead_stato_cambiato: 'swap_horiz',
  pratica_creata: 'description',
  stato_iniziale: 'inventory_2',
  stato_cambiato: 'swap_horiz',
  appuntamento_fissato: 'event',
  task_creato: 'add_task',
  task_completato: 'task_alt',
  documento_caricato: 'upload_file',
}

function TouchBlock({ title, touch, empty }: { title: string; touch: AttributionTouch | null; empty: string }) {
  return (
    <Card variant="filled" padding="sm">
      <h3 className="md-typescale-title-small mb-1">{title}</h3>
      {touch === null ? (
        <div className="text-muted md-typescale-body-small">{empty}</div>
      ) : (
        <KeyValueList
          className="md-typescale-body-small"
          items={[
            { label: 'Fonte', value: <>{touch.source ?? '—'}</> },
            { label: 'Campagna', value: <>{touch.utmCampaign ?? (touch.campaignId ? `#${touch.campaignId}` : '—')}</> },
            { label: 'Fornitore', value: <>{touch.supplier ?? '—'}</> },
            { label: 'UTM', value: <>{[touch.utmSource, touch.utmMedium].filter(Boolean).join(' / ') || '—'}</> },
            { label: 'Data lead', value: <Link to={`/leads/${touch.leadId}`}>{formatDay(touch.createdAt)}</Link> },
          ]}
        />
      )}
    </Card>
  )
}

/** Stesso controllo di CompanyDetailPage: i due ruoli che `PolicyNames.Admin` accetta lato server. */
function isAdmin(auth: LoginResponse): boolean {
  return auth.role === 'Administrator' || auth.role === 'EqualAdmin'
}

export function ContactDetailPage({ auth }: { auth: LoginResponse }) {
  const { id } = useParams()
  const contactId = Number(id)
  const archiveOn = useFeatureFlag('clienti.archivio_albero')
  const [data, setData] = useState<ContactOverview | null>(null)
  const [error, setError] = useState('')
  const [notFound, setNotFound] = useState(false)
  const [loading, setLoading] = useState(true)
  const [busyRule, setBusyRule] = useState<number | null>(null)
  const [message, setMessage] = useState('')
  const [revealKey, setRevealKey] = useState(0) // C-08: rimonta i campi dinamici dopo la visione motivata

  const load = useCallback(() => {
    if (!Number.isFinite(contactId)) {
      setNotFound(true)
      setLoading(false)
      return
    }
    setLoading(true)
    setError('')
    setNotFound(false)
    // F-108: la scheda 360 risponde 200 anche per un cliente nel cestino (con `deletedAt`): niente piu'
    // ripiego sull'anagrafica grezza.
    contactOverviewApi.get(contactId)
      .then(setData)
      .catch(e => {
        const text = e instanceof Error ? e.message : 'Errore nel caricamento della scheda cliente'
        // Il server risponde 404 sia per "non esiste" sia per "non lo vedi": stessa pagina per entrambi.
        if (/not found|404|non trovat/i.test(text)) setNotFound(true)
        else setError(text)
      })
      .finally(() => setLoading(false))
  }, [contactId])

  useEffect(() => { load() }, [load])

  async function createTask(action: NextAction) {
    if (action.ruleId === null) return
    setBusyRule(action.ruleId)
    setMessage('')
    try {
      const res = await contactOverviewApi.createCrossSellTask(contactId, action.ruleId)
      setMessage(res.created ? 'Attività creata.' : 'Esiste già un’attività per questo suggerimento, creata oggi.')
      load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossibile creare l’attività')
    } finally {
      setBusyRule(null)
    }
  }

  if (loading && !data) return <LoadingState />

  if (notFound) {
    return (
      <PageShell>
        <Callout tone="warning">Cliente non trovato o non accessibile.</Callout>
        <Link to="/customers">Torna ai clienti</Link>
      </PageShell>
    )
  }

  if (!data) return <Callout tone="error">{error || 'Scheda non disponibile'}</Callout>

  const c = data.contact
  // F-108: nel cestino la scheda e' in sola lettura; si puo' solo ripristinare (Admin).
  const trashed = c.deletedAt !== null

  return (
    <PageShell>
      {c.deletedAt !== null && (
        <TrashBanner
          kind="contacts"
          id={c.id}
          deletedAt={c.deletedAt}
          reason={c.deleteReason}
          canRestore={isAdmin(auth)}
          onRestored={load}
        />
      )}
      <PageHeader
        title={c.name}
        icon="person"
        back={{ to: '/customers', label: 'Clienti' }}
        subtitle={
          <>
            {c.contactType === 'company' ? 'Azienda' : c.contactType === 'person' ? 'Persona' : 'Cliente'}
            {' · '}Stato: {c.status}
            {c.assignedAgentName ? ` · Agente: ${c.assignedAgentName}` : ''}
          </>
        }
        actions={
          <>
            {/* F-117 (REQ-265-001): stampa e copia link, entrambe tracciate in audit. */}
            <PrintShareButtons entityType="Contact" entityId={c.id} />
            <RevealSensitiveButton entityType="contacts" entityId={c.id} values={[c.phone, c.mobile, c.fiscalCode]}
              onRevealed={() => { load(); setRevealKey(k => k + 1) }} />
            {!trashed && (
              <ButtonLink to={`/contacts/${c.id}/edit`} variant="outlined">Modifica anagrafica</ButtonLink>
            )}
            {!trashed && isAdmin(auth) && <MoveToTrashButton kind="contacts" id={c.id} onMoved={load} />}
          </>
        }
      />

      {error && <Callout tone="error">{error}</Callout>}
      {message && <Callout tone="success">{message}</Callout>}
      {c.mergedIntoContactId !== null && (
        <Callout tone="info">
          Questo contatto è stato unito a un altro: la scheda aggiornata è{' '}
          <Link to={`/contacts/${c.mergedIntoContactId}`}>contatto #{c.mergedIntoContactId}</Link>.
        </Callout>
      )}

      <PaneLayout
        variant="supporting"
        paneSide="start"
        main={
          <>
            <Card variant="outlined" padding="none">
              <CardHeader title="Azioni consigliate" />
              <List aria-label="Azioni consigliate">
                {data.actions.length === 0 && <ListItem headline={<span className="text-muted">Nessuna azione consigliata.</span>} />}
                {data.actions.map((a, i) => (
                  <ListItem
                    key={`${a.kind}-${a.refId ?? ''}-${i}`}
                    headline={
                      <>
                        <StatusPill tone={PRIORITY_TONE[a.priority]} className="me-2">{a.priority}</StatusPill>
                        <span className="md-typescale-emphasized-body-medium">{a.title}</span>
                      </>
                    }
                    supporting={`Perché: ${a.reason}`}
                    trailing={
                      <div className="text-nowrap">
                        {!trashed && a.kind === 'cross_sell' && a.ruleId !== null && (
                          <Button
                            variant="outlined"
                            size="sm"
                            disabled={busyRule === a.ruleId}
                            onClick={() => createTask(a)}
                          >
                            Crea attività
                          </Button>
                        )}
                        {a.refType === 'lead' && a.refId !== null && (
                          <ButtonLink variant="outlined" size="sm" to={`/leads/${a.refId}`}>Apri lead</ButtonLink>
                        )}
                        {a.contractId !== null && a.kind !== 'cross_sell' && (
                          <ButtonLink variant="outlined" size="sm" to={`/contracts/${a.contractId}`}>Apri pratica</ButtonLink>
                        )}
                      </div>
                    }
                  />
                ))}
              </List>
            </Card>

            {data.crossSell.length > 0 && (
              <Card variant="outlined" padding="none">
                <CardHeader title="Suggerimenti di cross-selling" />
                <List aria-label="Suggerimenti di cross-selling">
                  {data.crossSell.map(s => (
                    <ListItem
                      key={s.ruleId}
                      headline={
                        <>
                          <StatusPill tone={PRIORITY_TONE[s.priority]} className="me-2">{s.priority}</StatusPill>
                          {s.label}
                        </>
                      }
                      supporting={s.reason}
                    />
                  ))}
                </List>
              </Card>
            )}

            <Card variant="outlined" padding="none">
              <CardHeader
                icon="timeline"
                title={
                  <>
                    Cronologia <StatusPill tone="neutral">{data.timeline.items.length}</StatusPill>
                  </>
                }
              />
              <div>
                {data.timeline.items.length === 0 ? (
                  <div className="p-3 text-center text-muted md-typescale-body-small">Nessun evento registrato</div>
                ) : (
                  <List aria-label="Cronologia">
                    {data.timeline.items.map((it, i) => (
                      <ListItem
                        key={`${it.kind}-${it.refId ?? ''}-${it.occurredAt}-${i}`}
                        leading={<Icon name={KIND_ICONS[it.kind] ?? 'circle'} size={24} />}
                        headline={
                          <>
                            {it.title}
                            {it.isSnapshot && <StatusPill tone="neutral" className="ms-2">Fotografia iniziale</StatusPill>}
                            {it.contractId !== null && (
                              <Link className="ms-2" to={`/contracts/${it.contractId}`}>pratica</Link>
                            )}
                          </>
                        }
                        supporting={`${formatRome(it.occurredAt)}${it.actorName ? ` - ${it.actorName}` : it.actorUserId ? ` - Utente #${it.actorUserId}` : ''}`}
                      />
                    ))}
                  </List>
                )}
                {data.timeline.truncated && (
                  <div className="p-2 text-center text-muted md-typescale-body-small border-top">Mostrati i 500 eventi più recenti.</div>
                )}
              </div>
            </Card>
          </>
        }
        pane={
          <>
            <Card variant="outlined">
              <CardHeader title="Anagrafica" />
              <CardContent>
                <KeyValueList
                  className="md-typescale-body-small"
                  items={[
                    { label: 'Email', value: <>{c.email ?? '—'}</> },
                    { label: 'Telefono', value: <>{c.phone ?? c.mobile ?? '—'}</> },
                    { label: 'Codice fiscale', value: <>{c.fiscalCode ?? '—'}</> },
                    { label: 'Partita IVA', value: <>{c.vatNumber ?? '—'}</> },
                    { label: c.registeredSource.label, value: <span title={c.registeredSource.note}>{c.registeredSource.value ?? '—'}</span> },
                  ]}
                />
                <div className="text-muted md-typescale-body-small mt-2">{c.registeredSource.note}</div>
              </CardContent>
            </Card>

            <ContactDynamicFields key={revealKey} contactId={c.id} readOnly={trashed} />
            <CommercialBlockPanel contactId={c.id} auth={auth} />
            {!trashed && <ReferralPanel contactId={c.id} auth={auth} />}
            {!trashed && <UnoMobilePanel contactId={c.id} />}

            <Card variant="outlined">
              <CardHeader title="Attribuzione" />
              <CardContent>
                <CardGrid min={200} gap="collection">
                  <TouchBlock title="Prima attribuzione" touch={data.attribution.first} empty="Non determinabile" />
                  <TouchBlock title="Ultima attribuzione" touch={data.attribution.last} empty="Non determinabile" />
                </CardGrid>
                {data.attribution.notes.length > 0 && (
                  <ul className="md-typescale-body-small text-muted mt-2 mb-0">
                    {data.attribution.notes.map(n => <li key={n}>{n}</li>)}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardHeader title="Settori e prodotti posseduti" />
              <CardContent>
                {data.holdings.departments.length === 0 && data.holdings.products.length === 0 ? (
                  <div className="text-muted md-typescale-body-small">Nessun settore associato alle pratiche visibili.</div>
                ) : (
                  <>
                    <div className="d-flex flex-wrap gap-2">
                      {data.holdings.departments.map(d => (
                        <StatusPill key={d.departmentId} tone="primary">
                          {d.name} ({d.contracts})
                        </StatusPill>
                      ))}
                    </div>
                    {data.holdings.products.length > 0 && (
                      <div className="d-flex flex-wrap gap-2 mt-2">
                        {data.holdings.products.map(p => (
                          <StatusPill key={p.productId} tone="info">{p.name} ({p.contracts})</StatusPill>
                        ))}
                      </div>
                    )}
                  </>
                )}
                <div className="text-muted md-typescale-body-small mt-2">Calcolati sulle {data.holdings.scope}.</div>
              </CardContent>
            </Card>

            <Card variant="outlined" padding="none">
              <CardHeader title={`Pratiche (${data.contracts.length})`} />
              <List aria-label="Pratiche">
                {data.contracts.length === 0 && <ListItem headline={<span className="text-muted">Nessuna pratica visibile.</span>} />}
                {data.contracts.map(k => (
                  <ListItem
                    key={k.id}
                    headline={
                      <span>
                        <Link to={`/contracts/${k.id}`}>{k.referenceCode}</Link>
                        {k.departmentName ? ` · ${k.departmentName}` : ''}
                        {k.productName ? ` · ${k.productName}` : ''}
                        {` · ${formatDay(k.signedAt ?? k.createdAt)}`}
                      </span>
                    }
                    trailing={<StatusPill tone="neutral">{k.status}</StatusPill>}
                  />
                ))}
              </List>
            </Card>
            <ContactEarningsPanel contactId={c.id} />
            <CustomerValuePanel contactId={c.id} />
            {archiveOn && <ContactArchiveTree contactId={data.contact.id} />}
          </>
        }
      />
    </PageShell>
  )
}
